import { saveAs } from 'file-saver';
import { useCallback } from 'react';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import {
  ExternalIntegration,
  ExternalIntegrationDataSource,
} from 'dashboard/models/external-integration';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadImporter } from 'dashboard/redux/thunk-actions/importer';
import { cancelImporterRun, loadImporterRun } from 'dashboard/redux/thunk-actions/importer-run';
import DashboardImporterRunDetailsView from 'dashboard/views/DashboardImporterRunDetailsView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import {
  ApiEndpoint,
  ExternalIntegrationDataSource as ApiModelExternalIntegrationDataSource,
} from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

type DashboardImporterRunDetailsRouteProps = PropsFromRedux;

type Params = { importerId: string; importerRunId: string };

const DashboardImporterRunDetailsRoute = (props: DashboardImporterRunDetailsRouteProps) => {
  const dispatch = useDispatch();
  const params = useParams<Params>() as Params; // Will always be defined

  const { loading: isLoadingImporter } = useApiLoader({
    loader: async () => {
      props.loadImporter(params.importerId);
    },
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load importer, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
    deps: [props.loadImporter, props.showMessage],
  });

  const { loading: isLoadingImporterRun } = useApiLoader({
    loader: async () => props.loadImporterRun(params.importerRunId),
    deps: [props.loadImporterRun],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load importer run.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: cancelImporterRunError, callback: handleCancelImporterRun } =
    useApiCallback(async () => {
      await props.cancelImporterRun(params.importerRunId);
      props.showMessage('Canceled Importer Run.', MessageType.Success);
    }, [props.cancelImporterRun, props.showMessage]);

  useApiErrorHandler({
    error: cancelImporterRunError,
    onError: () => props.showMessage('Failed to cancel importer run.', MessageType.Failure),
    onNetworkError: () => props.showMessage('Failed to cancel importer run.', MessageType.Failure),
  });

  const handleDownloadErrors = useCallback(async () => {
    try {
      const blob = await SessionClientDashboard.getBlob(
        ApiEndpoint.ImporterRun,
        params.importerRunId,
        'export-errors',
      );
      saveAs(blob, 'error-summary.csv');
    } catch (e) {
      dispatch(showMessage('Failed to download CSV', MessageType.Failure));
    }
  }, [dispatch, params.importerRunId]);

  const handleDownloadSuccesses = useCallback(async () => {
    try {
      const blob = await SessionClientDashboard.getBlob(
        ApiEndpoint.ImporterRun,
        params.importerRunId,
        'export-successes',
      );
      saveAs(blob, 'success-summary.csv');
    } catch (e) {
      dispatch(showMessage('Failed to download CSV', MessageType.Failure));
    }
  }, [dispatch, params.importerRunId]);

  const isDataSource = (e: ExternalIntegration): e is ExternalIntegrationDataSource => {
    return ApiModelExternalIntegrationDataSource.includes(e.type);
  };
  const dataSources = props.externalIntegrations.filter(isDataSource);
  const dataSource = dataSources.find((ds) => {
    return props.importer?.relationships?.['external-integration']?.data?.id === ds.id;
  });

  if (
    isLoadingImporter ||
    isLoadingImporterRun ||
    !props.importer?.attributes ||
    !props.importerRun?.attributes ||
    !dataSource
  ) {
    return (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    );
  } else {
    return (
      <DashboardImporterRunDetailsView
        cancelImporterRun={handleCancelImporterRun}
        dataSource={dataSource}
        handleDownloadErrors={handleDownloadErrors}
        handleDownloadSuccesses={handleDownloadSuccesses}
        importer={props.importer}
        importerRun={props.importerRun}
      />
    );
  }
};

const mapStateToProps = (state: RootState) => {
  return {
    importer: state.importer.importer,
    importerRun: state.importerRun.importerRun,
    externalIntegrations: state.externalIntegrations.externalIntegrations,
  };
};

const mapDispatchToProps = {
  cancelImporterRun,
  loadImporter,
  loadImporterRun,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardImporterRunDetailsRoute);
